import React, { useEffect, useState, lazy, Suspense } from "react";
import { useLocation } from "react-router-dom";
import { useAuth0 } from "./react-auth0-spa";
import {
  AppsHub,
  Dashboard,
  GiottoAiHub,
  CDNBrowser,
  InternalDrive,
  CompanyReports,
  CreateBackup,
  CreatePaymentPage,
  DocScanner,
  Documents,
  DownloadCollection,
  GiottoAnnunciFunebri,
  GiottoBackgrounds,
  GiottoClientProjects,
  GiottoImages,
  GiottoSymbols,
  GiottoTags,
  GiottoProducts,
  IframePage,
  ImageServicePlayground,
  MappaClienti,
  MappaPreventivi,
  ModuleRevenue,
  PlatformStorage,
  PrivateRoute,
  RestoreBackups,
  Sidebar,
  StatusGrid,
  SystemEvents,
  TestReports,
} from "./components";
import Allegati from "./components/Allegati";
import { BrowserRouter, Redirect, Switch, Route } from "react-router-dom";
import { Spin, Layout } from "antd";
import EasterEgg from "react-easter";
import {
  QueryParamProvider,
  transformSearchStringJsonSafe,
} from "use-query-params";
import { SendNotifications } from "./components/SendNotifications";
import { Servizi } from "./components/Servizi";
import { Features, Guides, Icons, Modules } from "./components/Settings";
import {
  MarketplaceSellers,
  MarketplaceSellerDetails,
  MarketplaceOrgs,
  MarketplaceOrgDetails,
  MarketplaceCategories,
  MarketplaceFeatures,
  MarketplaceTypologies,
} from "./components";

// Lazy load components that are heavy or not immediately needed
const OrgsV2 = lazy(() => import("./components/OrgsV2"));
const OrgDetailsV2 = lazy(() => import("./components/OrgDetailsV2"));
const Users = lazy(() => import("./components/Users"));
const Abbonamenti = lazy(() => import("./components/Abbonamenti"));
const Tos = lazy(() => import("./components/Tos"));

// Before conamiCode declaration, add a helper component for iframes that handles mobile devices better
const ResponsiveIframePage = ({ title, src }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (isMobile) {
    return (
      <div style={{ padding: "20px" }}>
        <h2>{title}</h2>
        <p>This content may be resource-intensive on mobile devices.</p>
        <a
          href={src}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: "block",
            margin: "20px auto",
            padding: "10px 15px",
            backgroundColor: "#3D5864",
            color: "white",
            borderRadius: "4px",
            textAlign: "center",
            maxWidth: "300px",
          }}
        >
          Click to open in new tab
        </a>
      </div>
    );
  }

  return <IframePage title={title} src={src} />;
};

const konamiCode = ["w", "i", "n", "w", "i", "n"];

const youtubeVideos = [
  "https://www.youtube.com/embed/uhscMsBhNhw?autoplay=1",
  "https://www.youtube.com/embed/DLzxrzFCyOs?autoplay=1",
];

const { Content } = Layout;

function GiottoAiHubPage() {
  return <GiottoAiHub mode="global" />;
}

function GiottoAiLegacyRedirect({ location, view }) {
  const params = new URLSearchParams(location.search);
  params.set("view", view);

  return (
    <Redirect
      to={{
        pathname: "/giotto/ai",
        search: `?${params.toString()}`,
      }}
    />
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function App() {
  const { loading, isAuthenticated, getTokenSilently } = useAuth0();
  const [token, setToken] = useState();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (loading) {
      return;
    }

    if (!isAuthenticated) {
      setToken(undefined);
      return;
    }

    const fetchToken = async () => {
      try {
        const token = await getTokenSilently();
        setToken(token);
      } catch (error) {
        if (error?.error === "login_required") {
          setToken(undefined);
          return;
        }

        console.error("Error fetching token:", error);
      }
    };
    fetchToken();
  }, [getTokenSilently, isAuthenticated, loading]);

  const queryStringifyOptions = {
    transformSearchString: transformSearchStringJsonSafe,
  };


  if (loading) {
    return (
      <div
        style={{
          width: "100vw",
          height: "100vh",
          position: "absolute",
          zIndex: 999,
          textAlign: "center",
          marginTop: "20%",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <QueryParamProvider
          ReactRouterRoute={Route}
          stringifyOptions={queryStringifyOptions}
        >
          <Layout style={{ minHeight: "100vh" }}>
            <Sidebar />
            <Layout className="site-layout">
              <Content
                style={{
                  padding: isMobile ? 0 : "20px",
                }}
              >
                <Suspense
                  fallback={
                    <Spin
                      size="large"
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        padding: "50px",
                      }}
                    />
                  }
                >
                  <Switch>
                    <PrivateRoute path="/" exact component={Dashboard} />
                    <PrivateRoute path="/apps" component={AppsHub} />
                    <PrivateRoute path="/ci" component={StatusGrid} />
                    <PrivateRoute
                      path="/mappa"
                      component={() => (
                        <ResponsiveIframePage
                          title="Mappa Agenzie Funebri Italiane"
                          src="https://www.google.com/maps/d/embed?mid=1rDmaRP71B2RX9WT2d4V3L2VNonxLn0Ac&hl=it"
                        />
                      )}
                    />
                    <PrivateRoute
                      path="/mappa-clienti"
                      component={MappaClienti}
                    />
                    <PrivateRoute
                      path="/mappa-preventivi"
                      component={MappaPreventivi}
                    />
                    <PrivateRoute
                      path="/analytics/crediti-ai"
                      exact
                      render={(routeProps) => (
                        <GiottoAiLegacyRedirect {...routeProps} view="credits" />
                      )}
                    />
                    <PrivateRoute
                      path="/analytics/crediti-ai-charts"
                      exact
                      render={(routeProps) => (
                        <GiottoAiLegacyRedirect {...routeProps} view="statistics" />
                      )}
                    />
                    <PrivateRoute
                      path="/analytics/acquisto-addon"
                      exact
                      render={(routeProps) => (
                        <GiottoAiLegacyRedirect {...routeProps} view="purchases" />
                      )}
                    />
                    <PrivateRoute
                      path="/analytics/abbonamenti"
                      component={Abbonamenti}
                    />
                    <PrivateRoute
                      path="/analytics/storage"
                      component={PlatformStorage}
                    />
                    <PrivateRoute
                      path="/analytics/usage"
                      component={() => (
                        <ResponsiveIframePage
                          title="Platform Usage Overview"
                          src="https://analytics.zoho.eu/open-view/276819000000009928"
                        />
                      )}
                    />
                    <PrivateRoute
                      path="/system-events"
                      component={SystemEvents}
                    />
                    <PrivateRoute
                      path="/test-reports/:project/:timestamp"
                      component={TestReports}
                    />
                    <PrivateRoute
                      path="/test-reports/:project"
                      component={TestReports}
                    />
                    <PrivateRoute
                      path="/test-reports"
                      component={TestReports}
                    />
                    <PrivateRoute
                      path="/cdn-browser"
                      component={CDNBrowser}
                    />
                    <PrivateRoute
                      path="/internal-drive"
                      component={InternalDrive}
                    />
                    <PrivateRoute
                      path="/allegati"
                      component={Allegati}
                    />
                    <PrivateRoute
                      path="/giotto/editor_images_default"
                      component={GiottoImages}
                    />
                    <PrivateRoute
                      path="/giotto/editor_backgrounds_default"
                      component={GiottoBackgrounds}
                    />
                    <PrivateRoute
                      path="/giotto/editor_symbols_default"
                      component={GiottoSymbols}
                    />
                    <PrivateRoute
                      path="/giotto/tags"
                      component={GiottoTags}
                    />
                    <PrivateRoute
                      path="/giotto/prodotti"
                      component={GiottoProducts}
                    />
                    <PrivateRoute
                      path="/giotto/client-projects"
                      component={GiottoClientProjects}
                    />
                    <PrivateRoute
                      path="/giotto/ai"
                      component={GiottoAiHubPage}
                    />
                    <PrivateRoute
                      path="/giotto/annunci-funebri"
                      component={GiottoAnnunciFunebri}
                    />
                    <PrivateRoute
                      path="/db/backup/restore"
                      render={(routeProps) => (
                        <RestoreBackups {...routeProps} accessToken={token} />
                      )}
                    />
                    <PrivateRoute
                      path="/db/backup/new"
                      render={(routeProps) => (
                        <CreateBackup {...routeProps} accessToken={token} />
                      )}
                    />
                    <PrivateRoute
                      path="/db/backup/download"
                      render={(routeProps) => (
                        <DownloadCollection
                          {...routeProps}
                          accessToken={token}
                        />
                      )}
                    />
                    <PrivateRoute
                      path="/sales/create-payment-page"
                      component={CreatePaymentPage}
                    />
                    <PrivateRoute
                      path="/sales/create-quote"
                      component={() => (
                        <ResponsiveIframePage
                          title="Platform Usage Overview"
                          src="https://forms.zohopublic.eu/ofc/form/NuovoContattoRapidoMC/formperma/SfWV-Lb5aThkbJY18mI9WK-O3plCE9pBX6HTpJ33gIA"
                        />
                      )}
                    />
                    <PrivateRoute
                      path="/sales/create-contact"
                      component={() => (
                        <ResponsiveIframePage
                          title="Crea contatto"
                          src="https://share-eu1.hsforms.com/1mv_6vWr6QDeUSG9CKq7rpAf1gx3"
                        />
                      )}
                    />
                    <PrivateRoute path="/documents" component={Documents} />
                    <PrivateRoute path="/doc-scanner" component={DocScanner} />
                    <PrivateRoute
                      path="/send-notifications"
                      component={SendNotifications}
                    />
                    <PrivateRoute
                      path="/tools/image-service-playground"
                      component={ImageServicePlayground}
                    />
                    <PrivateRoute path="/servizi" component={Servizi} />
                    <PrivateRoute
                      path="/company-reports"
                      component={CompanyReports}
                    />
                    <PrivateRoute
                      path="/admin/bi/module-revenue"
                      component={ModuleRevenue}
                    />
                    <PrivateRoute
                      path="/settings/features"
                      component={Features}
                    />
                    <PrivateRoute
                      path="/settings/guides"
                      component={Guides}
                    />
                    <PrivateRoute
                      path="/settings/modules"
                      component={Modules}
                    />
                    <PrivateRoute
                      path="/settings/icons"
                      component={Icons}
                    />
                    <PrivateRoute
                      path="/orgs/:orgId/:tabName"
                      component={OrgDetailsV2}
                    />
                    <PrivateRoute
                      path="/orgs/:orgId"
                      component={OrgDetailsV2}
                    />
                    <PrivateRoute path="/orgs" component={OrgsV2} />
                    <PrivateRoute path="/users" component={Users} />
                    <PrivateRoute
                      path="/funeralcloud/sellers/:sellerId/:tabName"
                      component={MarketplaceSellerDetails}
                    />
                    <PrivateRoute
                      path="/funeralcloud/sellers/:sellerId"
                      component={MarketplaceSellerDetails}
                    />
                    <PrivateRoute
                      path="/funeralcloud/sellers"
                      component={MarketplaceSellers}
                    />
                    <PrivateRoute
                      path="/funeralcloud/orgs/:orgId/:tabName"
                      component={MarketplaceOrgDetails}
                    />
                    <PrivateRoute
                      path="/funeralcloud/orgs/:orgId"
                      component={MarketplaceOrgDetails}
                    />
                    <PrivateRoute
                      path="/funeralcloud/orgs"
                      component={MarketplaceOrgs}
                    />
                    <PrivateRoute
                      path="/funeralcloud/categories"
                      component={MarketplaceCategories}
                    />
                    <PrivateRoute
                      path="/funeralcloud/features"
                      component={MarketplaceFeatures}
                    />
                    <PrivateRoute
                      path="/funeralcloud/typologies"
                      component={MarketplaceTypologies}
                    />
                    <PrivateRoute path="/tos" component={Tos} />
                  </Switch>
                </Suspense>
              </Content>
            </Layout>
          </Layout>
        </QueryParamProvider>
      </BrowserRouter>
      {!isMobile && (
        <EasterEgg keys={konamiCode} timeout={10000}>
          <iframe
            title="oooops"
            src={
              youtubeVideos[Math.floor(Math.random() * youtubeVideos.length)]
            }
            frameborder="0"
            allowfullscreen
          />
        </EasterEgg>
      )}
    </div>
  );
}

export default App;
